import { PRIVATE_SALE_LIST_CARD_COPY } from '@unreserved/constants';
import type { PropertyPricingProps } from '@unreserved/types';
import { formatCurrency, parsePriceAmount } from './format';

export type ResolvedPropertyCardPricing =
  | { kind: 'currency'; amount: number; suffixLabel: string }
  | { kind: 'price_text'; text: string }
  | { kind: 'display_price'; text: string };

export function formatPropertyCardListPrice(amount: number): string {
  if (!Number(amount)) {
    return '';
  }
  return formatCurrency(amount, 'AUD', 'en-AU', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  } as const);
}

export function resolvePropertyCardPricing(
  input: PropertyPricingProps,
): ResolvedPropertyCardPricing {
  const {
    saleType,
    startingBid,
    startingPrice,
    priceText,
    displayPrice,
    isVendorManagedCampaign,
    modalFlowPropertyCard = false,
  } = input;

  if (modalFlowPropertyCard) {
    const priceTextTrimmed = typeof priceText === 'string' ? priceText.trim() : '';
    if (priceTextTrimmed) return { kind: 'price_text', text: priceTextTrimmed };
    if (!displayPrice) {
      return {
        kind: 'display_price',
        text: isVendorManagedCampaign
          ? PRIVATE_SALE_LIST_CARD_COPY.CONTACT_VENDOR
          : PRIVATE_SALE_LIST_CARD_COPY.CONTACT_UNRESERVED,
      };
    }
    const amount = parsePriceAmount(startingPrice);
    if (amount === undefined) {
      const amountText =
        (typeof startingPrice === 'string' && startingPrice.trim()) || '';
      if (amountText) return { kind: 'price_text', text: amountText };
    }
    return {
      kind: 'currency',
      amount: amount ?? 0,
      suffixLabel: 'Starting Price',
    };
  }

  if (saleType === 'Private Sale') {
    // Shared: when priceText is set, buyers and owners see the same text.
    const priceTextTrimmed = typeof priceText === 'string' ? priceText.trim() : '';
    if (priceTextTrimmed) return { kind: 'price_text', text: priceTextTrimmed };

    // Shared: when displayPrice is off, buyers and owners see the same contact text.
    if (!displayPrice) {
      return {
        kind: 'display_price',
        text: isVendorManagedCampaign
          ? PRIVATE_SALE_LIST_CARD_COPY.CONTACT_VENDOR
          : PRIVATE_SALE_LIST_CARD_COPY.CONTACT_UNRESERVED,
      };
    }

    return {
      kind: 'currency',
      amount: parsePriceAmount(startingPrice) ?? 0,
      suffixLabel: 'Starting Price',
    };
  }

  // Live Auction only: prefer priceText over numeric starting bid.
  // Reverse Auction always shows the starting bid amount.
  if (saleType === 'Live Auction') {
    const priceTextTrimmed = typeof priceText === 'string' ? priceText.trim() : '';
    if (priceTextTrimmed) return { kind: 'price_text', text: priceTextTrimmed };
  }

  const amount = parsePriceAmount(startingPrice) ?? parsePriceAmount(startingBid);
  const suffixLabel = startingBid ? 'Starting Bid' : 'Starting Price';
  return {
    kind: 'currency',
    amount: amount as number,
    suffixLabel,
  };
}
